import { Location, NavigateFunction } from 'react-router-dom';

import { Journey } from 'Global/Journey/Types/Journey';
import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import { isStandbyPopupRequired } from 'Modules/Standby/Utils/isStandbyPopupRequired';
import {
	canNavigateToCartStep,
	canNavigateToPaymentStep,
} from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { getLowerCaseMessageByKey, getPascalCaseMessageByKey } from 'Stores/LanguageStore';
import { checkIfInvalidName } from 'Util/StringUtils';
import { isNotNullOrUndefined, stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import {
	convertWeight,
	EXACT_WEIGHT_MAX_STORABLE,
	roundWeightForUnit,
	toCanonicalKg,
	weightUnitLabel,
} from 'Util/Weight';
import { getAddOnsTabVisibility, getExistingAddOnIds } from 'Util/_HumanWritten/BookingWizard/BookingWizardWrapUtils';
import { IsValidPhone } from 'Validators/Functions/HumanWritten/Phone';
import AddOnsTab from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/AddOn/AddOnTab';
import { BookingWizardTab, SidebarStatus, VisibilityStatus } from '../BookingWizard';
import { BookingWizardTabNames } from '../BookingWizardConstants';
import { BookingWizardData, BookingWizardInnerDataType } from '../BookingWizardData/BaseBookingWizardData';
import { PassengerBookingWizardData } from '../BookingWizardData/PassengerBookingWizardData';
import { VehicleBookingWizardData } from '../BookingWizardData/VehicleBookingWizardData';
import { WizardErrors } from '../BookingWizardTypes';
import { SelectedTrips } from '../BookingWizardWrap';
import { combineAllPassengerTickets, validatePassengers } from './Passengers/PassengerTabHelpers';
import { PassengersTab } from './Passengers/PassengersTab';
import { VehicleTab } from './Vehicle/VehicleTab';
import { getCenteredPageContentClass } from './BulkBooking/utils';
import { useDynamicBookingFieldsStore } from 'Stores/DynamicBookingFieldsStore';
import { validateDynamicFields } from './BookingDetails/DynamicFieldsSection';

export function getVehicleBookingTabs(
	wizardData: BookingWizardData,
	setBaseWizardErrors: (errors: WizardErrors<BookingWizardData>) => void,
	setInnerWizardErrors: (errors: WizardErrors<BookingWizardInnerDataType>) => void,
	selectedTrips: SelectedTrips,
	isStaff: boolean,
	location: Location,
	navigate: NavigateFunction,
	oldWizardData?: BookingWizardData | null,
	// The booking subject's customer type (selected customer for staff-on-behalf) —
	// resolved reactively by the caller so the tabs rebuild when it lands.
	subjectUserDisplayNameId: string | null = null,
): BookingWizardTab[] {
	const existingAddOnIds = getExistingAddOnIds(wizardData, oldWizardData);
	return [
		{
			name: BookingWizardTabNames.Vehicle,
			displayName: getPascalCaseMessageByKey('vehicle__name__label', 'Vehicle'),
			path: 'vehicle',
			tabTitle: `${getPascalCaseMessageByKey('vehicle__name__label', 'Vehicle')} details`,
			tabTitleEdit: `Edit ${getLowerCaseMessageByKey('vehicle__name__label', 'Vehicle')} details`,
			visibility: wizardData.tabSelected === 'vehicle' ? VisibilityStatus.VISIBLE : VisibilityStatus.EXCLUDED,
			sidebarStatus: wizardData.isBulkBookingFlow ? SidebarStatus.NO_SIDEBAR : SidebarStatus.CART_SUMMARY,
			validateStep: validateVehicleTab({
				wizardData,
				setBaseWizardErrors,
				setInnerWizardErrors,
				location,
				navigate,
				subjectUserDisplayNameId,
			}),
			contentClass: wizardData.isBulkBookingFlow ? getCenteredPageContentClass() : undefined,
			component: <VehicleTab isCustomer={!isStaff} selectedTrips={selectedTrips} />,
			authorize: true,
			showTAndCsButton: false,
		},
		{
			name: BookingWizardTabNames.Passengers,
			displayName: 'Passengers',
			path: 'passengers',
			tabTitle: 'Passengers',
			tabTitleEdit: 'Edit Passengers',
			visibility: appConfigStore.showPassengerTab(wizardData)
				? VisibilityStatus.VISIBLE
				: VisibilityStatus.EXCLUDED,
			sidebarStatus: wizardData.isBulkBookingFlow ? SidebarStatus.NO_SIDEBAR : SidebarStatus.CART_SUMMARY,
			validateStep: updateErrorState => {
				const newBaseErrors: WizardErrors<BookingWizardData> = {};
				const newInnerErrors: WizardErrors<VehicleBookingWizardData | PassengerBookingWizardData> = {};

				if (canNavigateToCartStep(location) || canNavigateToPaymentStep(location)) {
					if (updateErrorState) {
						setBaseWizardErrors(newBaseErrors);
					}
					return true;
				}

				const errors = validatePassengers(combineAllPassengerTickets(wizardData));
				if (isNotNullOrUndefined(errors) && errors.length > 0) {
					newInnerErrors.adultTickets = 'Required field';
				}

				if (updateErrorState) {
					setInnerWizardErrors(newInnerErrors);
				}
				return Object.keys(newInnerErrors).length === 0 && Object.keys(newBaseErrors).length === 0;
			},
			contentClass: wizardData.isBulkBookingFlow ? getCenteredPageContentClass() : undefined,
			component: <PassengersTab />,
			authorize: true,
			showTAndCsButton: false,
		},
		{
			name: BookingWizardTabNames.AddOns,
			displayName: 'Add ons',
			path: 'add-ons',
			tabTitle: 'Add-ons',
			tabTitleEdit: 'Edit add-ons',
			visibility: getAddOnsTabVisibility(wizardData.tabSelected, selectedTrips, existingAddOnIds),
			sidebarStatus: wizardData.isBulkBookingFlow ? SidebarStatus.NO_SIDEBAR : SidebarStatus.CART_SUMMARY,
			validateStep: () => true,
			contentClass: wizardData.isBulkBookingFlow ? getCenteredPageContentClass() : undefined,
			component: (
				<>
					<AddOnsTab selectedTrips={selectedTrips} bookingType="vehicle" />
				</>
			),
			authorize: true,
			showTAndCsButton: false,
		},
	];
}

interface ValidateVehicleTabArgs {
	wizardData: BookingWizardData;
	setBaseWizardErrors: (errors: WizardErrors<BookingWizardData>) => void;
	setInnerWizardErrors: (errors: WizardErrors<BookingWizardInnerDataType>) => void;
	location: Location;
	navigate: NavigateFunction;
	/** Booking subject's customer type — gates customer-type-restricted dynamic fields. */
	subjectUserDisplayNameId?: string | null;
}

const validateVehicleTab = ({
	wizardData,
	setBaseWizardErrors,
	setInnerWizardErrors,
	location,
	navigate,
	subjectUserDisplayNameId = null,
}: ValidateVehicleTabArgs) => {
	const { driverPhoneEnabled, driverPhoneRequired } = appConfigStore;

	return (updateErrorState: boolean) => {
		const newBaseErrors: WizardErrors<BookingWizardData> = {};
		const newInnerErrors: WizardErrors<VehicleBookingWizardData> = {};

		if (canNavigateToCartStep(location) || canNavigateToPaymentStep(location)) {
			if (updateErrorState) {
				setBaseWizardErrors(newBaseErrors);
			}
			return true;
		}

		if (wizardData.innerWizardData.type === 'vehicle') {
			if (stringIsEmpty(wizardData.innerWizardData.cargoIdentification)) {
				newInnerErrors.cargoIdentification = 'Required field';
			}

			if (stringIsEmpty(wizardData.innerWizardData.cargoMake)) {
				newInnerErrors.cargoMake = 'Required field';
			}

			if (stringIsEmpty(wizardData.innerWizardData.cargoModel)) {
				newInnerErrors.cargoModel = 'Required field';
			}

			if (stringIsEmpty(wizardData.innerWizardData.vehicleLengthId)) {
				newInnerErrors.vehicleLengthId = 'Required field';
			}

			if (
				appConfigStore.vehicleWidthEnabled &&
				wizardData.innerWizardData.showWidthDetails &&
				stringIsEmpty(wizardData.innerWizardData.vehicleWidthId)
			) {
				(newInnerErrors as any).vehicleWidthId = 'Required field';
			}

			if (
				appConfigStore.trailersEnabled &&
				wizardData.innerWizardData.trailerCheckboxSelected &&
				(wizardData.innerWizardData.trailerTypeId === 'NO_TRAILER' || !wizardData.innerWizardData.trailerTypeId)
			) {
				// #trailer-checkbox-deselect-on-no-trailer
				newInnerErrors.trailerTypeId = 'Required field';
			}

			// eslint-disable-next-line max-len
			if (
				wizardData.innerWizardData.trailerTypeId !== 'NO_TRAILER' &&
				stringIsEmpty(wizardData.innerWizardData.trailerLengthId) &&
				appConfigStore.trailersEnabled
			) {
				newInnerErrors.trailerLengthId = 'Required field';
			}

			// When the customer opts the trailer into a non-standard width, a width must be chosen
			// before continuing (mirrors the vehicle width requirement above). Skipped when the
			// vehicle/trailer widths are linked, because then the trailer follows the vehicle's width
			// and has no width of its own to select.
			if (
				appConfigStore.vehicleWidthEnabled &&
				!appConfigStore.vehicleAndTrailerWidthLinked &&
				wizardData.innerWizardData.trailerTypeId !== 'NO_TRAILER' &&
				!!wizardData.innerWizardData.trailerTypeId &&
				wizardData.innerWizardData.showTrailerWidthDetails &&
				stringIsEmpty(wizardData.innerWizardData.trailerWidthId)
			) {
				(newInnerErrors as any).trailerWidthId = 'Required field';
			}

			if (stringIsEmpty(wizardData.innerWizardData.driverFirstName)) {
				newInnerErrors.driverFirstName = 'Required field';
			} else if (checkIfInvalidName(wizardData.innerWizardData.driverFirstName ?? '')) {
				newInnerErrors.driverFirstName = "No spaces. A-Z and '-' only.";
			}

			if (stringIsEmpty(wizardData.innerWizardData.driverLastName)) {
				newInnerErrors.driverLastName = 'Required field';
			} else if (checkIfInvalidName(wizardData.innerWizardData.driverLastName ?? '')) {
				newInnerErrors.driverLastName = "No spaces. A-Z and '-' only.";
			}

			if (driverPhoneEnabled) {
				if (driverPhoneRequired && stringIsEmpty(wizardData.innerWizardData.driverPhone)) {
					newInnerErrors.driverPhone = 'Required field';
				} else if (
					!stringIsEmpty(wizardData.innerWizardData.driverPhone) &&
					!IsValidPhone(wizardData.innerWizardData.driverPhone)
				) {
					newInnerErrors.driverPhone = 'Enter a valid phone number';
				}
			}

			if (
				appConfigStore.vehicleNoteEnabled &&
				appConfigStore.vehicleNoteRequired &&
				stringIsEmpty(wizardData.innerWizardData.note)
			) {
				(newInnerErrors as any).note = 'Required field';
			}

			// Exact-weight required + above-max gating. The wizard input component
			// validates on every change, but if the user never touches the field
			// (e.g. it was never pre-filled because the cargo type lacks both an
			// explicit exact-weight default and a minimum band) the click-through
			// path bypasses validation. Re-check here as a hard gate.
			const displayUnit = appConfigStore.weightDisplayUnit;
			const vehicleCharacteristicIds =
				store.cargoTypeCharacteristicsMap[wizardData.innerWizardData.cargoTypeId ?? ''] ?? [];
			if (appConfigStore.cargoTypeRequiresExactWeight(vehicleCharacteristicIds)) {
				const vehicleWeight = wizardData.innerWizardData.vehicleExactWeight;
				if (vehicleWeight === null || vehicleWeight === undefined || Number.isNaN(vehicleWeight)) {
					(newInnerErrors as any).vehicleExactWeight = 'Required field';
				} else {
					const maxKg = appConfigStore.vehicleExactWeightMaximumKg;
					if (maxKg !== null && toCanonicalKg(vehicleWeight, displayUnit) > maxKg) {
						const maxInDisplay = convertWeight(maxKg, 'kg', displayUnit);
						(newInnerErrors as any).vehicleExactWeight =
							`Must be no more than ${roundWeightForUnit(maxInDisplay, displayUnit)} ${weightUnitLabel(displayUnit)}`;
					}
				}
			}

			const trailerTypeId = wizardData.innerWizardData.trailerTypeId;
			if (appConfigStore.trailersEnabled && trailerTypeId && trailerTypeId !== 'NO_TRAILER') {
				const trailerCharacteristicIds = store.towOnTypeCharacteristicsMap?.[trailerTypeId] ?? [];
				if (appConfigStore.towOnTypeRequiresExactWeight(trailerCharacteristicIds)) {
					const trailerWeight = wizardData.innerWizardData.trailerExactWeight;
					if (trailerWeight === null || trailerWeight === undefined || Number.isNaN(trailerWeight)) {
						(newInnerErrors as any).trailerExactWeight = 'Required field';
					} else {
						const maxKg = appConfigStore.trailerExactWeightMaximumKg;
						if (maxKg !== null && toCanonicalKg(trailerWeight, displayUnit) > maxKg) {
							const maxInDisplay = convertWeight(maxKg, 'kg', displayUnit);
							(newInnerErrors as any).trailerExactWeight =
								`Must be no more than ${roundWeightForUnit(maxInDisplay, displayUnit)} ${weightUnitLabel(displayUnit)}`;
						}
					}
				}
			}

			// Hard database ceiling for exact weights. ExactWeight is persisted as a
			// numeric(10,3) column (in the entered unit), so any value above
			// EXACT_WEIGHT_MAX_STORABLE overflows the column and fails the save with a
			// 500. Enforce it unconditionally — independent of whether the type requires
			// an exact weight and of the (optional) configured maximum — so a stale value
			// carried over from another type, or a missing config max, can't slip through.
			const vehicleWeightValue = wizardData.innerWizardData.vehicleExactWeight;
			if (
				isNotNullOrUndefined(vehicleWeightValue) &&
				!Number.isNaN(vehicleWeightValue) &&
				vehicleWeightValue > EXACT_WEIGHT_MAX_STORABLE
			) {
				(newInnerErrors as any).vehicleExactWeight =
					`Must be no more than ${EXACT_WEIGHT_MAX_STORABLE} ${weightUnitLabel(displayUnit)}`;
			}
			const trailerWeightValue = wizardData.innerWizardData.trailerExactWeight;
			if (
				isNotNullOrUndefined(trailerWeightValue) &&
				!Number.isNaN(trailerWeightValue) &&
				trailerWeightValue > EXACT_WEIGHT_MAX_STORABLE
			) {
				(newInnerErrors as any).trailerExactWeight =
					`Must be no more than ${EXACT_WEIGHT_MAX_STORABLE} ${weightUnitLabel(displayUnit)}`;
			}

			// Combined vehicle + trailer cap. Only meaningful when both exact weights are
			// present, and only checked once each field is otherwise valid (so the more
			// specific per-field errors surface first). Reported under a dedicated key — not
			// a field key — so it blocks navigation without leaving a stale per-field error
			// behind when only one weight is later lowered. The matching inline message is
			// rendered live on both the vehicle and trailer inputs.
			const combinedMaxKg = appConfigStore.combinedExactWeightMaximumKg;
			if (
				combinedMaxKg !== null &&
				isNotNullOrUndefined(vehicleWeightValue) &&
				!Number.isNaN(vehicleWeightValue) &&
				isNotNullOrUndefined(trailerWeightValue) &&
				!Number.isNaN(trailerWeightValue) &&
				!(newInnerErrors as any).vehicleExactWeight &&
				!(newInnerErrors as any).trailerExactWeight
			) {
				const combinedKg =
					toCanonicalKg(vehicleWeightValue, displayUnit) + toCanonicalKg(trailerWeightValue, displayUnit);
				if (combinedKg > combinedMaxKg) {
					const maxInDisplay = convertWeight(combinedMaxKg, 'kg', displayUnit);
					(newInnerErrors as any).combinedExactWeight =
						`Combined vehicle and trailer weight must be no more than ${roundWeightForUnit(maxInDisplay, displayUnit)} ${weightUnitLabel(displayUnit)}`;
				}
			}

			// Admin-configured dynamic fields targeted at VEHICLE bookings.
			// We use the booking-subject's UserDisplayName id so customer-type-
			// restricted required fields are enforced (matches what the UI shows).
			const dynamicFields = useDynamicBookingFieldsStore.getState().fields;
			const dynamicErrors = validateDynamicFields(
				dynamicFields,
				'VEHICLE',
				subjectUserDisplayNameId,
				wizardData.innerWizardData.dynamicFieldValues,
			);
			if (Object.keys(dynamicErrors).length > 0) {
				(newInnerErrors as any).dynamicFieldValues = dynamicErrors;
			}

			if (updateErrorState) {
				setInnerWizardErrors(newInnerErrors);
			}

			const journeys: Journey[] = [];
			if (wizardData.departingJourney) {
				journeys.push(wizardData.departingJourney);
			}
			if (wizardData.returningJourney) {
				journeys.push(wizardData.returningJourney);
			}

			const standbyPopupRequired = isStandbyPopupRequired({
				journeys,
				cargoTypeId: wizardData.innerWizardData.cargoTypeId ?? '',
				navigate,
				defaultValues: {
					name: `${wizardData.innerWizardData.driverFirstName} ${wizardData.innerWizardData.driverLastName}`,
					phone: wizardData.innerWizardData.driverPhone,
					email: store.userData?.email,
					userId: wizardData.userId,
				},
			});

			if (standbyPopupRequired) {
				return false;
			}
		}

		return Object.keys(newInnerErrors).length === 0 && Object.keys(newBaseErrors).length === 0;
	};
};
